import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { FaExclamationTriangle } from "react-icons/fa";
import useU2000AxiosAuthenticated from "../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { useToast } from "../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { useU2000SearchHandlers } from "../../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../../u2000Core/reducer/formReducer/U2000GridState";
import {
  U2000GridStateDeleteAction,
  type U2000GridStateNextRecordAfterDeleteReturn,
} from "../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { U2000FormSBusinessHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import u2000FormState from "../../../u2000Core/reducer/formReducer/U2000FormState";
import U2000DefaultForm from "../../../u2000Core/components/formControls/U2000DefaultForm";
import U2000Tab from "../../../u2000Core/components/tab/U2000Tab";
import U2000BasicDialog from "../../../u2000Core/components/dialog/U2000BasicDialog";
import U2000ActionBtn from "../../../u2000Core/components/buttons/U2000ActionBtn";
import { useU2000LogicalUnitContext } from "../../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import { useU2000AuthContext } from "../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000MinMdViewport } from "../../../u2000Core/hooks/useU2000MinMdViewport";
import U3130TimesheetFormHeaderTab from "./U3130TimesheetFormHeaderTab";
import { U3130TimesheetWeekViewProvider } from "./U3130TimesheetWeekViewContext";
import U3130HonorariumTab from "./U3130HonorariumTab";
import U3130AbsencesBanquesTab from "./U3130AbsencesBanquesTab";
import U3130ExpenseTab from "./U3130ExpenseTab";
import U3130EquipmentTab from "./U3130EquipmentTab";
import { u3130FormatPeriodDateRange } from "../../tools/u3130FormatPeriodDateRange";
import { ensureBankHoursList, normalizeTimesheetFromApi, type U3130Timesheet } from "../../U3130TimesheetModel";
import { useU3130Context } from "../../U3130Context";
import { doGet, doGetAuditsData, doSearch, luHandleApiError, type U3130SearchPayload } from "../../U3130Service";
import {
  u2000ComputeTimesheetDerivedTotals,
  u2000MergeTimesheetDerivedTotalsInto,
  u2000TimesheetTotalWorkedHours,
} from "../../../u2000Core/tools/U2000TimesheetFormDerivedTotals";
import { u2000FormatTimesheetPeriodForBreadcrumb } from "../../../u2000Core/tools/U2000TimesheetPeriodBreadcrumbFormat";
import { u3130MapApprovalListPresetToApi } from "../../tools/u3130ApprovalListPreset";
import { honorariaTabHasValidationErrors } from "../../tools/u3130ValidateHonorariaLines";
import { bankHoursTabHasValidationErrors } from "../../tools/u3130ValidateBankHoursLines";
import { U2000_TIMESHEET_WEEK_DAYS } from "../../../u2000Core/tools/U2000TimesheetHonorariumDayTotals";
import { expensesTabHasValidationErrors } from "../../tools/u3130ValidateExpenseLines";
import { equipmentTabHasValidationErrors } from "../../tools/u3130ValidateEquipmentLines";
import { U3130_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS, u3130HideExpenseAndEquipmentTabs } from "../../tools/u3130HideExpenseAndEquipmentTabs";
import U2000AuditPanelComponent from "../../../u2000Core/components/audit/U2000AuditPanelComponent";
import { NS, U3130ROLES } from "../../U3130";
import { useU3130ApproveHandlers, type U3130ApproveOptionalPayload } from "../../U3130UseApproveHandlers";
import { useU3130RefuseHandlers } from "../../U3130UseRefuseHandlers";
import { useU3130BeginCorrectionHandlers } from "../../U3130UseBeginCorrectionHandlers";
import U3130TimesheetDetailToolBar from "./U3130TimesheetDetailToolBar";
import {
  u3130ApproveDialogHasHourlyExcess,
  u3130ApproveDialogShowExcessPanels,
  u3130ComputeApproveDialogHoursCentiGap,
  u3130ComputeApproveDialogOpenFields,
  u3130FormatApproveHoursGapForLocale,
  u3130IsApproveDialogHoursBalanced,
  u3130ParseApproveDialogDecimal,
} from "../../tools/u3130ApproveDialogHours";
import U3130ApproveDialogPanel from "../common/U3130ApproveDialogPanel";

function toFiniteNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

const U3130_FORM_TAB_LIST_COLLAPSED_STORAGE_KEY = "u3130-form-tab-list-collapsed";

const U3130_APPROVE_DIALOG_BANK_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

function u3130ApproveDialogRowTotalWeekHours(row: Partial<Record<(typeof U3130_APPROVE_DIALOG_BANK_DAYS)[number], unknown>> | undefined): number {
  if (!row) return 0;
  return U3130_APPROVE_DIALOG_BANK_DAYS.reduce((acc, d) => acc + (Number(row[d]) || 0), 0);
}

function formatApproveAttentionDecimalHours(hours: number, lang: string): string {
  const dec = lang.toLowerCase().startsWith("fr") ? "," : ".";
  return hours.toFixed(2).replace(".", dec);
}

/** Fil d’Ariane détail : segment employé (après le titre LU) puis période + statut (période sans jour de la semaine). */
function buildU3130TimesheetDetailBreadcrumbTrail(
  clean: U3130Timesheet,
  normalized: Pick<U3130Timesheet, "id" | "periodDate">,
  locale: string,
): { employeeLabel?: string; detailLabel: string } {
  const statusPart = clean.statusDisplay ? ` (${clean.statusDisplay})` : "";
  const periodIso = (normalized.periodDate ?? "").trim();
  const periodText =
    periodIso.length >= 10
      ? u2000FormatTimesheetPeriodForBreadcrumb(periodIso, locale)
      : (clean.periodDisplay || periodIso).trim();
  const detailLabel = periodText ? `${periodText}${statusPart}` : `${normalized.id ?? ""}${statusPart}`;
  const employeeLabel = (clean.employeeDisplay ?? "").trim() || undefined;
  return { employeeLabel, detailLabel };
}

const U3130TimesheetDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const { hasRole } = useU2000AuthContext();
  const minMdViewport = useU2000MinMdViewport();
  const formTabListOrientation = minMdViewport ? "vertical" : "horizontal";
  const [formTabListCollapsed, setFormTabListCollapsed] = useState(() => {
    try {
      return localStorage.getItem(U3130_FORM_TAB_LIST_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(U3130_FORM_TAB_LIST_COLLAPSED_STORAGE_KEY, String(formTabListCollapsed));
    } catch {
      /* localStorage indisponible */
    }
  }, [formTabListCollapsed]);
  const { searchStateHandlers, gridStateHandlers, luStateHandlers, approvalListPreset, formActiveTab, setFormActiveTab, initLU } = useU3130Context();
  const hideExpenseAndEquipmentTabs = u3130HideExpenseAndEquipmentTabs(initLU.personalization);
  const { approve: approveRequest } = useU3130ApproveHandlers();
  const { refuse: refuseRequest } = useU3130RefuseHandlers();
  const { beginCorrection: beginCorrectionRequest } = useU3130BeginCorrectionHandlers();

  const [formState, formStateDispatch] = useReducer(u2000FormState, u2000IntFormState);
  const [approveLoading, setApproveLoading] = useState(false);
  const [refuseLoading, setRefuseLoading] = useState(false);
  const [modifyLoading, setModifyLoading] = useState(false);
  const [approveDlgOpen, setApproveDlgOpen] = useState(false);
  const [refuseDlgOpen, setRefuseDlgOpen] = useState(false);
  /** Totaux heures (dialogue Approuver — panneau commun). */
  const [approveSalaryTotalHours, setApproveSalaryTotalHours] = useState("");
  const [approveSalaryPaidHours, setApproveSalaryPaidHours] = useState("");
  const [approveSalaryUnpaidHours, setApproveSalaryUnpaidHours] = useState("");
  /** Dépôt banque d’heures spéciale (dialogue Approuver). */
  const [approveHourlyRegularToBank, setApproveHourlyRegularToBank] = useState("");
  const [approveHourlyHalfToBank, setApproveHourlyHalfToBank] = useState("");
  const [approveHourlyDoubleToBank, setApproveHourlyDoubleToBank] = useState("");
  /** BL-0637 — aide repliée par panneau du dialogue Approuver. */
  const [approveDlgGeneralHelpOpen, setApproveDlgGeneralHelpOpen] = useState(false);
  const [approveDlgHourlyHelpOpen, setApproveDlgHourlyHelpOpen] = useState(false);
  const [approveDlgSalaryHelpOpen, setApproveDlgSalaryHelpOpen] = useState(false);
  const [approvalQueueExhausted, setApprovalQueueExhausted] = useState(false);

  const buildUiTimesheet = useCallback(
    (normalized: U3130Timesheet): U3130Timesheet => {
      const employeeDisplay = normalized.employee
        ? [normalized.employee.firstName, normalized.employee.lastName].filter(Boolean).join(" ")
        : "";
      const periodDisplay = u3130FormatPeriodDateRange(normalized.periodDate ?? "");
      const statusI18nKey = normalized.status ? `status${normalized.status}` : "";
      const statusDisplay = statusI18nKey
        ? t(statusI18nKey, { ns: NS, defaultValue: normalized.status })
        : (normalized.status ?? "");

      return {
        ...normalized,
        employeeDisplay,
        periodDisplay,
        statusDisplay,
      };
    },
    [t],
  );

  const u3130TimesheetBL: U2000FormSBusinessHandlers<U3130Timesheet> = {
    cancelForm: () => {
      if (luStateHandlers.getActionOriginFrom() === "/U3130") {
        navigate("/U3130", { replace: true });
      }
    },
    onLeaveForm: () => navigate("/U3130"),
    onChangeRecordForm: (row) => {
      if (row?.id) {
        handleGet.getData(row.id);
      }
    },
    onChangePageForm: (page, direction) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction,
      );
    },
  };

  const formStateHandlers = useU2000FormStateHandlers<U3130Timesheet>({
    formState,
    formStateDispatch,
    businessHandlers: u3130TimesheetBL,
  });

  const handleGet = useU2000GetHandlers<U3130Timesheet>({
    doGetFn: (idParam) => doGet(u2000AxiosAuthenticated, idParam),
    onSuccess: (row) => {
      if (row == null || !(row as { id?: string }).id?.trim()) {
        toast.error(
          t("msgDetailLoadInvalid", { ns: NS, defaultValue: "Réponse invalide pour cette feuille de temps." }),
          t("errorTitle", { ns: NS }),
        );
        return;
      }
      const normalized = normalizeTimesheetFromApi(row as U3130Timesheet);
      const base = buildUiTimesheet(normalized);
      const clean =
        "undefined" in base
          ? (() => {
              const rest = Object.fromEntries(
                Object.entries(base as Record<string, unknown>).filter(([key]) => key !== "undefined"),
              );
              return rest as unknown as U3130Timesheet;
            })()
          : base;
      const withTotals = u2000MergeTimesheetDerivedTotalsInto(clean);
      if (normalized.id) {
        gridStateHandlers.setDataById(normalized.id, withTotals);
      }
      formStateHandlers.setData(withTotals);
      if (normalized.id) {
        const newPath = location.pathname.replace(/(?:create|[\w-]+)$/, normalized.id);
        if (newPath !== location.pathname) {
          navigate(newPath, { replace: true });
        }
      }
      const { employeeLabel, detailLabel } = buildU3130TimesheetDetailBreadcrumbTrail(clean, normalized, i18n.language);
      setLogicalUnitBreadcrumb([
        { href: "/U3130", label: t("title", { ns: NS }) },
        ...(employeeLabel ? [{ label: employeeLabel }] : []),
        { label: detailLabel },
      ]);
    },
    onError: ({ error }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
    },
  });

  const handleSearch = useU2000SearchHandlers<U3130Timesheet>({
    doSearchFn: async (searchPayload: U2000SearchPayload) => {
      const extended: U3130SearchPayload = {
        ...searchPayload,
        approvalListPreset: u3130MapApprovalListPresetToApi(approvalListPreset),
      };
      return await doSearch(u2000AxiosAuthenticated, extended);
    },
    onSuccess: (searchResult: U2000SearchResult<U3130Timesheet>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
      const nextRow = searchResult.data[idx];
      if (nextRow?.id) {
        handleGet.getData(nextRow.id);
      }
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  useEffect(() => {
    if (!id?.trim()) return;
    setApprovalQueueExhausted(false);
    void handleGet.getData(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const advanceAfterApprovalAction = useCallback(
    (processedId: string, mode: "approve" | "refuse", approvedTimesheet?: U3130Timesheet) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      newData = newData.filter((row) => row.id !== processedId);
      totalRows = Math.max(0, totalRows - 1);
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(
        currentIdx,
        newData,
        totalRows,
      );

      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        setApprovalQueueExhausted(false);
        void handleGet.getData(action.id);
        navigate(`/U3130/${action.id}`, { replace: true });
        return;
      }
      if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page != null) {
        setApprovalQueueExhausted(false);
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
        return;
      }
      if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page != null) {
        setApprovalQueueExhausted(false);
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
        return;
      }

      if (mode === "approve" && approvedTimesheet?.id?.trim()) {
        setApprovalQueueExhausted(false);
        const normalized = normalizeTimesheetFromApi(approvedTimesheet);
        const withTotals = u2000MergeTimesheetDerivedTotalsInto(buildUiTimesheet(normalized));
        gridStateHandlers.setDataById(normalized.id!, withTotals);
        formStateHandlers.setData(withTotals);
        navigate(`/U3130/${approvedTimesheet.id}`, { replace: true });
        const { employeeLabel, detailLabel } = buildU3130TimesheetDetailBreadcrumbTrail(
          withTotals,
          normalized,
          i18n.language,
        );
        setLogicalUnitBreadcrumb([
          { href: "/U3130", label: t("title", { ns: NS }) },
          ...(employeeLabel ? [{ label: employeeLabel }] : []),
          { label: detailLabel },
        ]);
        return;
      }

      setApprovalQueueExhausted(true);
    },
    [
      buildUiTimesheet,
      formStateHandlers,
      gridStateHandlers,
      handleGet,
      handleSearch,
      i18n.language,
      navigate,
      searchStateHandlers,
      setLogicalUnitBreadcrumb,
      t,
    ],
  );

  const data = formStateHandlers.getData();

  useEffect(() => {
    if (data == null) return;

    const derived = u2000ComputeTimesheetDerivedTotals(data);

    const needsUpdate =
      toFiniteNumber(data.totalPaidHours) !== derived.totalPaidHours ||
      toFiniteNumber(data.totalLeaveHours) !== derived.totalLeaveHours ||
      toFiniteNumber(data.totalSpecialWithdraw) !== derived.totalSpecialWithdraw ||
      toFiniteNumber(data.totalSpecialDeposit) !== derived.totalSpecialDeposit ||
      toFiniteNumber(data.totalSpecialNet) !== derived.totalSpecialNet ||
      toFiniteNumber(data.totalExpenses) !== derived.totalExpenses ||
      toFiniteNumber(data.totalEquipment) !== derived.totalEquipment ||
      toFiniteNumber(data.approvalPaidHours) !== derived.approvalPaidHours;

    if (!needsUpdate) return;

    formStateHandlers.setData({
      ...data,
      ...derived,
    });
  }, [data, formStateHandlers]);

  const runApprove = useCallback(async () => {
    const row = formStateHandlers.getData();
    if (!row?.id?.trim() || row.status !== "WaitingForApproval") return;
    if (
      !u3130IsApproveDialogHoursBalanced(
        approveSalaryTotalHours,
        approveSalaryPaidHours,
        approveSalaryUnpaidHours,
        approveHourlyRegularToBank,
        approveHourlyHalfToBank,
        approveHourlyDoubleToBank,
      )
    ) {
      return;
    }
    setApproveLoading(true);
    try {
      const totalWorkedForApprove = u3130ParseApproveDialogDecimal(approveSalaryTotalHours);
      const optionalPayload: U3130ApproveOptionalPayload = {
        totalHours: totalWorkedForApprove,
        totalPaidHours: u3130ParseApproveDialogDecimal(approveSalaryPaidHours),
        unpaidHours: u3130ParseApproveDialogDecimal(approveSalaryUnpaidHours),
        regularHoursToBank: u3130ParseApproveDialogDecimal(approveHourlyRegularToBank),
        halfTimeHoursToBank: u3130ParseApproveDialogDecimal(approveHourlyHalfToBank),
        doubleTimeHoursToBank: u3130ParseApproveDialogDecimal(approveHourlyDoubleToBank),
      };
      const approved = await approveRequest(row.id, row.version, optionalPayload);
      toast.success(t("msgApproveSuccess", { ns: NS, defaultValue: "Feuille de temps approuvée." }));
      advanceAfterApprovalAction(row.id, "approve", approved);
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    } finally {
      setApproveLoading(false);
      setApproveDlgOpen(false);
    }
  }, [
    advanceAfterApprovalAction,
    approveRequest,
    approveHourlyDoubleToBank,
    approveHourlyHalfToBank,
    approveHourlyRegularToBank,
    approveSalaryPaidHours,
    approveSalaryTotalHours,
    approveSalaryUnpaidHours,
    formStateHandlers,
    t,
    toast,
  ]);

  const openApproveDialog = useCallback(() => {
    const row = formStateHandlers.getData();
    if (row) {
      const open = u3130ComputeApproveDialogOpenFields(
        row.employee?.remunerationType,
        toFiniteNumber(row.employee?.hourlyBase),
        u2000TimesheetTotalWorkedHours(row),
        {
          regularHoursToBank: toFiniteNumber(row.regularHoursToBank),
          halfTimeHoursToBank: toFiniteNumber(row.halfTimeHoursToBank),
          doubleTimeHoursToBank: toFiniteNumber(row.doubleTimeHoursToBank),
        },
      );
      setApproveSalaryTotalHours(String(open.totalHours));
      setApproveSalaryPaidHours(String(open.totalPaidHours));
      setApproveSalaryUnpaidHours(String(open.unpaidHours));
      setApproveHourlyRegularToBank(String(open.regularHoursToBank));
      setApproveHourlyHalfToBank(String(open.halfTimeHoursToBank));
      setApproveHourlyDoubleToBank(String(open.doubleTimeHoursToBank));
    }
    setApproveDlgGeneralHelpOpen(false);
    setApproveDlgHourlyHelpOpen(false);
    setApproveDlgSalaryHelpOpen(false);
    setApproveDlgOpen(true);
  }, [formStateHandlers]);

  const runRefuse = useCallback(async () => {
    const row = formStateHandlers.getData();
    if (!row?.id?.trim() || row.status !== "WaitingForApproval") return;
    setRefuseLoading(true);
    try {
      await refuseRequest(row.id, row.version);
      toast.success(t("msgRefuseSuccess", { ns: NS, defaultValue: "Feuille de temps refusée (statut Ouvert)." }));
      advanceAfterApprovalAction(row.id, "refuse");
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    } finally {
      setRefuseLoading(false);
      setRefuseDlgOpen(false);
    }
  }, [advanceAfterApprovalAction, formStateHandlers, refuseRequest, t, toast]);

  const runBeginCorrection = useCallback(async () => {
    const row = formStateHandlers.getData();
    if (!row?.id?.trim() || row.status !== "WaitingForApproval") return;
    setModifyLoading(true);
    try {
      const saved = await beginCorrectionRequest(row.id, row.version);
      const returnPath = `/U3130/${row.id}`;
      navigate(`/U3129/${row.id}`, {
        state: {
          approvalCorrection: true,
          returnPath,
          version: saved?.version,
        },
      });
    } catch (error: unknown) {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleApiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    } finally {
      setModifyLoading(false);
    }
  }, [beginCorrectionRequest, formStateHandlers, navigate, t, toast]);

  const honorariaCount = data?.honoraria?.length ?? 0;
  const absencesBanquesCount = useMemo(() => {
    const list = ensureBankHoursList(data?.bankHoursList);
    return list.filter((row) => U2000_TIMESHEET_WEEK_DAYS.some((d) => (Number(row[d]) || 0) > 0)).length;
  }, [data?.bankHoursList]);
  const expensesCount = data?.expenses?.length ?? 0;
  const equipmentCount = data?.equipment?.length ?? 0;

  const formTabs: { id: string; label: React.ReactNode; content: React.ReactNode; collapsedRailLabel: string; collapsedRailTitle: string }[] = [
    {
      id: "tabHonoraria",
      collapsedRailLabel: t("tabHonorariaRailAbbrev", { ns: NS, defaultValue: "Hon." }),
      collapsedRailTitle: t("tabHonoraria", { ns: NS, defaultValue: "Honoraires" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabHonoraria", { ns: NS, defaultValue: "Honoraires" })}
          {honorariaCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{honorariaCount}</span>}
        </span>
      ),
      content: <U3130HonorariumTab formStateHandlers={formStateHandlers} />,
    },
    {
      id: "tabAbsencesBanques",
      collapsedRailLabel: t("tabAbsencesBanquesRailAbbrev", { ns: NS, defaultValue: "Abs." }),
      collapsedRailTitle: t("tabAbsencesBanques", { ns: NS, defaultValue: "Absences et banques" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabAbsencesBanques", { ns: NS, defaultValue: "Absences et banques" })}
          {absencesBanquesCount > 0 && <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">{absencesBanquesCount}</span>}
        </span>
      ),
      content: <U3130AbsencesBanquesTab formStateHandlers={formStateHandlers} />,
    },
    {
      id: "tabExpenses",
      collapsedRailLabel: t("tabExpensesRailAbbrev", { ns: NS, defaultValue: "Dép." }),
      collapsedRailTitle: t("tabExpenses", { ns: NS, defaultValue: "Dépenses" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabExpenses", { ns: NS, defaultValue: "Dépenses" })}
          {expensesCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{expensesCount}</span>}
        </span>
      ),
      content: <U3130ExpenseTab formStateHandlers={formStateHandlers} />,
    },
    {
      id: "tabEquipment",
      collapsedRailLabel: t("tabEquipmentRailAbbrev", { ns: NS, defaultValue: "Éq." }),
      collapsedRailTitle: t("tabEquipment", { ns: NS, defaultValue: "Équipement" }),
      label: (
        <span className="inline-flex items-center gap-1.5">
          {t("tabEquipment", { ns: NS, defaultValue: "Équipement" })}
          {equipmentCount > 0 && <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-700">{equipmentCount}</span>}
        </span>
      ),
      content: <U3130EquipmentTab formStateHandlers={formStateHandlers} />,
    },
    ...(hasRole(U3130ROLES.AUDITS) && data?.id
      ? [
          {
            id: "tabAudits",
            collapsedRailLabel: t("tabAuditsRailAbbrev", { ns: NS, defaultValue: "Aud." }),
            collapsedRailTitle: t("tabAudits", { ns: NS, defaultValue: "Audits" }),
            label: t("tabAudits", { ns: NS, defaultValue: "Audits" }),
            content: <U2000AuditPanelComponent id={data.id} doGetAuditsData={doGetAuditsData} toastName="U3130TimesheetAudit" />,
          },
        ]
      : []),
  ];

  const visibleFormTabs = hideExpenseAndEquipmentTabs
    ? formTabs.filter((tab) => !(U3130_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS as readonly string[]).includes(tab.id))
    : formTabs;

  useEffect(() => {
    if (!hideExpenseAndEquipmentTabs) {
      return;
    }
    if ((U3130_HIDDEN_EXPENSE_EQUIPMENT_TAB_IDS as readonly string[]).includes(formActiveTab)) {
      setFormActiveTab("tabHonoraria");
    }
  }, [hideExpenseAndEquipmentTabs, formActiveTab, setFormActiveTab]);

  const tabsWithErrors = useMemo(() => {
    const e = formState.errors as Record<string, string>;
    const tabs: string[] = [];
    if (honorariaTabHasValidationErrors(e)) tabs.push("tabHonoraria");
    if (bankHoursTabHasValidationErrors(e)) tabs.push("tabAbsencesBanques");
    if (!hideExpenseAndEquipmentTabs && expensesTabHasValidationErrors(e)) tabs.push("tabExpenses");
    if (!hideExpenseAndEquipmentTabs && equipmentTabHasValidationErrors(e)) tabs.push("tabEquipment");
    return tabs;
  }, [formState.errors, hideExpenseAndEquipmentTabs]);

  const approveDialogHoursCentiGap = useMemo(
    () =>
      u3130ComputeApproveDialogHoursCentiGap(
        approveSalaryTotalHours,
        approveSalaryPaidHours,
        approveSalaryUnpaidHours,
        approveHourlyRegularToBank,
        approveHourlyHalfToBank,
        approveHourlyDoubleToBank,
      ),
    [
      approveSalaryTotalHours,
      approveSalaryPaidHours,
      approveSalaryUnpaidHours,
      approveHourlyRegularToBank,
      approveHourlyHalfToBank,
      approveHourlyDoubleToBank,
    ],
  );

  const approveDialogHoursBalanceOk = approveDialogHoursCentiGap === 0;

  const approveDialogHoursGapDisplay = useMemo(
    () => u3130FormatApproveHoursGapForLocale(approveDialogHoursCentiGap, i18n.language),
    [approveDialogHoursCentiGap, i18n.language],
  );

  const approveDialogHasHourlyExcess = useMemo(
    () =>
      u3130ApproveDialogHasHourlyExcess(
        data?.employee?.hourlyBase,
        u3130ParseApproveDialogDecimal(approveSalaryTotalHours),
      ),
    [approveSalaryTotalHours, data?.employee?.hourlyBase],
  );

  const approveDialogShowExcessPanels = useMemo(
    () =>
      u3130ApproveDialogShowExcessPanels(
        approveDialogHasHourlyExcess,
        approveSalaryTotalHours,
        approveSalaryPaidHours,
      ),
    [approveDialogHasHourlyExcess, approveSalaryPaidHours, approveSalaryTotalHours],
  );

  /** Avertissements métier dans le dialogue d’approbation (vacances négatives, congé autorisé gestionnaire). */
  const approveDialogPayrollAttention = useMemo(() => {
    const ts = data;
    const bankList = ensureBankHoursList(ts?.bankHoursList);
    const vacationRow = bankList.find((b) => b.bankId === "vacation");
    const authorizedRow = bankList.find((b) => b.bankId === "authorizedLeave");
    const vacationHoursRaw = u3130ApproveDialogRowTotalWeekHours(vacationRow);
    const vacationCenti = Math.round(vacationHoursRaw * 100);
    const authorizedHoursRaw = u3130ApproveDialogRowTotalWeekHours(authorizedRow);
    const authorizedCenti = Math.round(authorizedHoursRaw * 100);
    const vacationBefore =
      typeof ts?.vacationBalanceBeforeTimesheet === "number" && Number.isFinite(ts.vacationBalanceBeforeTimesheet)
        ? ts.vacationBalanceBeforeTimesheet
        : undefined;
    const vacationDebit =
      typeof ts?.vacationDebitHours === "number" && Number.isFinite(ts.vacationDebitHours)
        ? ts.vacationDebitHours
        : vacationCenti / 100;
    const vacationAfter =
      typeof ts?.vacationBalanceAfterTimesheet === "number" && Number.isFinite(ts.vacationBalanceAfterTimesheet)
        ? ts.vacationBalanceAfterTimesheet
        : vacationBefore !== undefined
          ? vacationBefore - vacationDebit
          : undefined;
    const vacationThreshold =
      typeof ts?.vacationThresholdHours === "number" && Number.isFinite(ts.vacationThresholdHours)
        ? ts.vacationThresholdHours
        : typeof ts?.employee?.vacationNegativeThresholdHours === "number" && Number.isFinite(ts.employee.vacationNegativeThresholdHours)
          ? ts.employee.vacationNegativeThresholdHours
          : 0;
    const vacationExceeded =
      typeof ts?.vacationThresholdExceededHours === "number" && Number.isFinite(ts.vacationThresholdExceededHours)
        ? ts.vacationThresholdExceededHours
        : vacationAfter !== undefined
          ? Math.max(0, -vacationAfter - Math.max(0, vacationThreshold))
          : 0;
    const negativeVacation =
      (ts?.vacationBankWillBeNegative === true || (vacationAfter !== undefined && vacationAfter < 0)) && vacationDebit > 0;
    const hasAuthorizedLeave = authorizedCenti > 0;
    return {
      show: negativeVacation || hasAuthorizedLeave,
      negativeVacation,
      vacationBalanceBefore: vacationBefore,
      vacationDebitHours: vacationDebit,
      vacationBalanceAfter: vacationAfter,
      vacationThresholdHours: Math.max(0, vacationThreshold),
      vacationThresholdExceededHours: vacationExceeded,
      authorizedHours: hasAuthorizedLeave ? authorizedCenti / 100 : 0,
    };
  }, [data]);

  const routeId = id?.trim() ?? "";
  const recordId = (data?.id ?? "").trim();
  const detailMatchesRoute = routeId !== "" && recordId === routeId;

  if (!routeId) {
    return null;
  }

  if (approvalQueueExhausted) {
    return (
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <U2000DefaultForm<U3130Timesheet>
          fillMainContentHeight
          searchStateHandlers={searchStateHandlers}
          gridStateHandlers={gridStateHandlers}
          formStateHandlers={formStateHandlers}
        >
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <p className="text-sm text-gray-700">
              {t("msgApprovalQueueEmpty", {
                ns: NS,
                defaultValue: "Toutes les feuilles de la file ont été traitées. Utilisez le bouton Liste pour revenir à la grille.",
              })}
            </p>
          </div>
        </U2000DefaultForm>
      </div>
    );
  }

  if (!detailMatchesRoute) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-600">{t("msgLoading", { ns: NS, defaultValue: "Chargement..." })}</p>
      </div>
    );
  }

  const salaryFieldClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-hidden focus:ring-2 focus:ring-blue-500/20";
  const salaryFieldDisabledClass =
    "w-full cursor-not-allowed rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600";

  /** Employé à l’heure en premier si `Hourly`, sinon employé à salaire en premier (`Salary` ou type inconnu). */
  const approveHourlyPanelFirst = data.employee?.remunerationType === "Hourly";

  const approveDialogSalaryPanel = (
    <U3130ApproveDialogPanel
      panelId="u3130-approve-salary"
      title={t("dlgApproveSalarySection", { ns: NS })}
      titleClassName="text-amber-950"
      panelClassName="border-amber-100 bg-amber-50/80"
      helpExpanded={approveDlgSalaryHelpOpen}
      onToggleHelp={() => setApproveDlgSalaryHelpOpen((v) => !v)}
      helpContent={<p className="text-amber-900/90">{t("dlgApproveSalaryPanelHint", { ns: NS })}</p>}
    >
      <div className="max-w-xs flex flex-col gap-1">
        <label htmlFor="u3130-approve-unpaid-hours" className="text-xs font-medium text-gray-700">
          {t("dlgApproveSalaryUnpaidHours", { ns: NS })}
        </label>
        <input
          id="u3130-approve-unpaid-hours"
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={approveSalaryUnpaidHours}
          onChange={(e) => setApproveSalaryUnpaidHours(e.target.value)}
          className={salaryFieldClass}
        />
      </div>
    </U3130ApproveDialogPanel>
  );

  const approveDialogHourlyPanel = (
    <U3130ApproveDialogPanel
      panelId="u3130-approve-hourly"
      title={t("dlgApproveHourlyPanelSection", { ns: NS })}
      titleClassName="text-sky-900"
      panelClassName="border-sky-100 bg-sky-50/80"
      helpExpanded={approveDlgHourlyHelpOpen}
      onToggleHelp={() => setApproveDlgHourlyHelpOpen((v) => !v)}
      helpContent={<p className="text-sky-800/90">{t("dlgApproveHourlyPanelHint", { ns: NS })}</p>}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="u3130-approve-regular-bank" className="text-xs font-medium text-gray-700">
            {t("dlgApproveHourlySimpleHours", { ns: NS })}
          </label>
          <input
            id="u3130-approve-regular-bank"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={approveHourlyRegularToBank}
            onChange={(e) => setApproveHourlyRegularToBank(e.target.value)}
            className={salaryFieldClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="u3130-approve-half-bank" className="text-xs font-medium text-gray-700">
            {t("dlgApproveHourlyHalfHours", { ns: NS })}
          </label>
          <input
            id="u3130-approve-half-bank"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={approveHourlyHalfToBank}
            onChange={(e) => setApproveHourlyHalfToBank(e.target.value)}
            className={salaryFieldClass}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="u3130-approve-double-bank" className="text-xs font-medium text-gray-700">
            {t("dlgApproveHourlyDoubleHours", { ns: NS })}
          </label>
          <input
            id="u3130-approve-double-bank"
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={approveHourlyDoubleToBank}
            onChange={(e) => setApproveHourlyDoubleToBank(e.target.value)}
            className={salaryFieldClass}
          />
        </div>
      </div>
    </U3130ApproveDialogPanel>
  );

  return (
    <>
      <U2000BasicDialog
        isOpen={approveDlgOpen}
        onClose={() => setApproveDlgOpen(false)}
        title={t("dlgApproveTitle", { ns: NS })}
        width="large"
      >
        <div className="flex flex-col gap-4 px-6 py-4">
          <p className="text-sm text-gray-700">{t("dlgApproveBody", { ns: NS })}</p>
          {approveDialogPayrollAttention.show && (
            <div
              className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm text-violet-950 shadow-xs"
              role="region"
              aria-label={t("dlgApproveAttentionTitle", { ns: NS })}
            >
              <div className="flex gap-2.5">
                <FaExclamationTriangle className="mt-0.5 h-4 w-4 shrink-0 text-violet-700" aria-hidden />
                <div className="min-w-0 flex-1 space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-violet-900">
                    {t("dlgApproveAttentionTitle", { ns: NS })}
                  </p>
                  <p className="text-xs leading-snug text-violet-900/95">{t("dlgApproveAttentionIntro", { ns: NS })}</p>
                  <ul className="list-disc space-y-1.5 pl-4 text-xs leading-snug text-violet-950">
                    {approveDialogPayrollAttention.negativeVacation &&
                      approveDialogPayrollAttention.vacationThresholdExceededHours > 0.0001 && (
                        <li>
                          {t("dlgApproveAttentionVacationThresholdExceeded", {
                            ns: NS,
                            hours: formatApproveAttentionDecimalHours(
                              approveDialogPayrollAttention.vacationThresholdExceededHours,
                              i18n.language,
                            ),
                          })}
                        </li>
                      )}
                    {approveDialogPayrollAttention.negativeVacation && (
                      <li>
                        {t("dlgApproveAttentionVacationBalanceBreakdown", {
                          ns: NS,
                          before: formatApproveAttentionDecimalHours(
                            approveDialogPayrollAttention.vacationBalanceBefore ?? 0,
                            i18n.language,
                          ),
                          debit: formatApproveAttentionDecimalHours(
                            approveDialogPayrollAttention.vacationDebitHours,
                            i18n.language,
                          ),
                          after: formatApproveAttentionDecimalHours(
                            approveDialogPayrollAttention.vacationBalanceAfter ?? 0,
                            i18n.language,
                          ),
                          threshold: formatApproveAttentionDecimalHours(
                            approveDialogPayrollAttention.vacationThresholdHours,
                            i18n.language,
                          ),
                        })}
                      </li>
                    )}
                    {approveDialogPayrollAttention.authorizedHours > 0 && (
                      <li>
                        {t("dlgApproveAttentionAuthorizedLeave", {
                          ns: NS,
                          hours: formatApproveAttentionDecimalHours(approveDialogPayrollAttention.authorizedHours, i18n.language),
                        })}
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <U3130ApproveDialogPanel
            panelId="u3130-approve-general"
            title={t("dlgApproveGeneralSection", { ns: NS })}
            titleClassName="text-gray-900"
            panelClassName="border-gray-200 bg-gray-50/90"
            helpExpanded={approveDlgGeneralHelpOpen}
            onToggleHelp={() => setApproveDlgGeneralHelpOpen((v) => !v)}
            helpContent={
              <>
                <p className="text-gray-700">{t("dlgApproveGeneralIntro", { ns: NS })}</p>
                <p className="border-l-4 border-amber-400 bg-amber-50/80 pl-3 pr-2 py-2 font-medium text-amber-950">
                  {t("dlgApproveGeneralBalanceImperative", { ns: NS })}
                </p>
                <p className="text-gray-600">{t("dlgApproveGeneralDefaults", { ns: NS })}</p>
              </>
            }
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="flex flex-col gap-1">
                <label htmlFor="u3130-approve-total-hours" className="text-xs font-medium text-gray-700">
                  {t("dlgApproveSalaryTotalHours", { ns: NS })}
                </label>
                <input
                  id="u3130-approve-total-hours"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={approveSalaryTotalHours}
                  disabled
                  className={salaryFieldDisabledClass}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label htmlFor="u3130-approve-paid-hours" className="text-xs font-medium text-gray-700">
                  {t("dlgApproveSalaryPaidHours", { ns: NS })}
                </label>
                <input
                  id="u3130-approve-paid-hours"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={approveSalaryPaidHours}
                  onChange={(e) => setApproveSalaryPaidHours(e.target.value)}
                  className={salaryFieldClass}
                />
              </div>
            </div>
          </U3130ApproveDialogPanel>
          {approveDialogShowExcessPanels &&
            (approveHourlyPanelFirst ? (
              <>
                {approveDialogHourlyPanel}
                {approveDialogSalaryPanel}
              </>
            ) : (
              <>
                {approveDialogSalaryPanel}
                {approveDialogHourlyPanel}
              </>
            ))}
          {!approveDialogHoursBalanceOk && (
            <p className="text-xs font-medium text-red-700" role="status">
              <Trans
                ns={NS}
                i18nKey="dlgApproveHoursMismatch"
                values={{ gap: approveDialogHoursGapDisplay }}
                components={{ bold: <strong className="font-bold text-red-900" /> }}
              />
            </p>
          )}
          <div className="flex justify-end gap-2">
            <U2000ActionBtn label={t("btnCancel", { ns: "common", defaultValue: "Annuler" })} handleClick={() => setApproveDlgOpen(false)} />
            <U2000ActionBtn
              label={t("btnApprove", { ns: NS })}
              handleClick={() => void runApprove()}
              disabled={approveLoading || !approveDialogHoursBalanceOk}
              className="bg-green-700! text-white! hover:bg-green-800!"
            />
          </div>
        </div>
      </U2000BasicDialog>

      <U2000BasicDialog isOpen={refuseDlgOpen} onClose={() => setRefuseDlgOpen(false)} title={t("dlgRefuseTitle", { ns: NS })} width="small">
        <div className="flex flex-col gap-4 px-6 py-4">
          <p className="text-sm text-gray-700">{t("dlgRefuseBody", { ns: NS })}</p>
          <div className="flex justify-end gap-2">
            <U2000ActionBtn label={t("btnCancel", { ns: "common", defaultValue: "Annuler" })} handleClick={() => setRefuseDlgOpen(false)} />
            <U2000ActionBtn
              label={t("btnRefuse", { ns: NS })}
              handleClick={() => void runRefuse()}
              disabled={refuseLoading}
              className="bg-amber-700! text-white! hover:bg-amber-800!"
            />
          </div>
        </div>
      </U2000BasicDialog>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <U2000DefaultForm<U3130Timesheet>
          fillMainContentHeight
          actionsBtns={
            <U3130TimesheetDetailToolBar
              data={data}
              onModifyClick={() => void runBeginCorrection()}
              onApproveClick={openApproveDialog}
              onRefuseClick={() => setRefuseDlgOpen(true)}
              modifyLoading={modifyLoading}
              approveLoading={approveLoading}
              refuseLoading={refuseLoading}
            />
          }
          searchStateHandlers={searchStateHandlers}
          gridStateHandlers={gridStateHandlers}
          formStateHandlers={formStateHandlers}
        >
          <div className="mt-1 flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden">
            <div className="shrink-0">
              <U3130TimesheetFormHeaderTab formStateHandlers={formStateHandlers} />
            </div>
            {visibleFormTabs.length > 0 && (
              <U3130TimesheetWeekViewProvider periodDate={data?.periodDate}>
              <U2000Tab
                tabs={visibleFormTabs}
                defaultTab="tabHonoraria"
                activeTab={formActiveTab}
                setActiveTab={(tab) => setFormActiveTab(typeof tab === "string" ? tab : String(tab))}
                tabsWithErrors={tabsWithErrors}
                fillRemainingHeight
                tabListOrientation={formTabListOrientation}
                activeIndicator={formTabListOrientation === "vertical" ? "border" : "underline"}
                tabPanelClassName={formTabListOrientation === "vertical" ? "p-4" : undefined}
                className="min-h-0 min-w-0 flex-1"
                tabListCollapsible={formTabListOrientation === "vertical"}
                tabListCollapsed={formTabListCollapsed}
                onTabListCollapsedChange={setFormTabListCollapsed}
                expandTabListLabel={t("btnExpandFormTabs", { ns: NS, defaultValue: "Développer les onglets" })}
                collapseTabListLabel={t("btnCollapseFormTabs", { ns: NS, defaultValue: "Replier les onglets" })}
              />
              </U3130TimesheetWeekViewProvider>
            )}
          </div>
        </U2000DefaultForm>
      </div>
    </>
  );
};

export default U3130TimesheetDetail;
